import { useState, useEffect } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchCuratedNPOs } from "@/lib/api/campaign";
import { useDebounce } from "@/hooks/use-debounce";
import { Search } from "lucide-react";

interface NPO {
  id: string;
  name: string;
  email: string;
  country: string;
  type: string;
  meta: {
    mobile?: string;
    address?: string;
    state_name?: string;
    district_name?: string;
    [key: string]: any;
  };
}

interface RFQGeneratorProps {
  campaign: {
    id: string;
    status?: string;
  };
  handleGenerateRFQ: (selectedNPOs: string[], additionalNotes?: string) => void;
  isGeneratingRFQ: boolean;
  token: string;
}

export function RFQGenerator({ campaign, handleGenerateRFQ, isGeneratingRFQ, token }: RFQGeneratorProps) {
  const [selectedNPOs, setSelectedNPOs] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const { ref, inView } = useInView();
  const [isSelectAll, setIsSelectAll] = useState(true);
  const [totalNPOCount, setTotalNPOCount] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState("");

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['curated-npos', debouncedSearchQuery],
    queryFn: ({ pageParam }) => {
      return fetchCuratedNPOs(token, pageParam, 10, debouncedSearchQuery);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage || !lastPage.pagination) {
        return undefined;
      }
      
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: !!token
  });

  // Update total NPO count and initialize selection only once
  useEffect(() => {
    if (data?.pages[0]?.pagination?.total && !hasInitialized) {
      setTotalNPOCount(data.pages[0].pagination.total);
      
      // Select all NPOs by default when data is first loaded
      if (isSelectAll) {
        const allLoadedNpoIds = data.pages.flatMap(page => 
          Array.isArray(page.data) ? page.data.map(npo => npo.id) : (page.data ? [page.data.id] : [])
        );
        setSelectedNPOs(allLoadedNpoIds);
        setHasInitialized(true);
      }
    }
  }, [data, isSelectAll, hasInitialized]);

  // Load next page when scrolling to the bottom
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Update selected NPOs when new pages are loaded and select all is active
  useEffect(() => {
    if (isSelectAll && data && hasInitialized) {
      const allLoadedNpoIds = data.pages.flatMap(page => 
        Array.isArray(page.data) ? page.data.map(npo => npo.id) : (page.data ? [page.data.id] : [])
      );
      setSelectedNPOs(allLoadedNpoIds);
    }
  }, [data, isSelectAll, hasInitialized]);

  const npos = data?.pages.flatMap(page => 
    Array.isArray(page.data) ? page.data : (page.data ? [page.data] : [])
  ) || [];

  const toggleNPO = (npoId: string) => {
    setSelectedNPOs(prev => 
      prev.includes(npoId) 
        ? prev.filter(id => id !== npoId) 
        : [...prev, npoId]
    );
    
    // Update select all state
    setIsSelectAll(false);
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      // Deselect all
      setSelectedNPOs([]);
      setIsSelectAll(false);
    } else {
      // Select all NPOs by ID
      const allNpoIds = npos.map(npo => npo.id);
      setSelectedNPOs(allNpoIds);
      setIsSelectAll(true);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Generate Request for Quotation (RFQ)</CardTitle>
        <CardDescription>
          Select NPOs to send RFQ for this campaign
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2 sticky top-0 bg-background z-10 py-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search NPOs..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button 
            variant="outline" 
            onClick={handleSelectAll}
          >
            {isSelectAll ? "Deselect All" : "Select All"}
          </Button>
        </div>

        <div className="border rounded-md">
          {isLoading ? (
            <div className="p-4 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-4 w-4" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-60" />
                  </div>
                </div>
              ))}
            </div>
          ) : npos.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">No NPOs found</p>
            </div>
          ) : (
            <div className="divide-y max-h-[400px] overflow-y-auto">
              {npos.map((npo: NPO) => (
                <div key={npo.id} className="p-4 flex items-start space-x-4">
                  <Checkbox
                    id={`npo-${npo.id}`}
                    checked={selectedNPOs.includes(npo.id)}
                    onCheckedChange={() => toggleNPO(npo.id)}
                  />
                  <div className="space-y-1">
                    <label
                      htmlFor={`npo-${npo.id}`}
                      className="font-medium cursor-pointer"
                    >
                      {npo.name}
                    </label>
                    <p className="text-sm text-muted-foreground">
                      {npo.meta.state_name}, {npo.meta.district_name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {npo.email} • {npo.meta.mobile || "No phone"}
                    </p>
                  </div>
                </div>
              ))}
              
              {/* Loading indicator for infinite scroll */}
              {(isFetchingNextPage || hasNextPage) && (
                <div
                  ref={ref}
                  className="p-4 flex justify-center"
                >
                  {isFetchingNextPage && (
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-60" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mt-4">
          <Label htmlFor="additional-notes" className="text-sm font-medium mb-1.5 block">
            Additional Notes or Requests
          </Label>
          <Textarea
            id="additional-notes"
            placeholder="Add any specific requirements, questions, or information you'd like NPOs to consider before responding to your RFQ..."
            className="min-h-[100px] resize-y"
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
          />
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <p className="text-sm text-muted-foreground">
          {isSelectAll 
            ? `All ${totalNPOCount} NPOs selected` 
            : `${selectedNPOs.length} NPOs selected`}
        </p>
        <Button 
          disabled={selectedNPOs.length === 0 && !isSelectAll || isGeneratingRFQ} 
          onClick={() => {
            // If select all is active, send empty array to indicate all NPOs
            if (isSelectAll) {
              handleGenerateRFQ([], additionalNotes);
            } else {
              handleGenerateRFQ(selectedNPOs, additionalNotes);
            }
          }}
        >
          Send RFQ Request
        </Button>
      </CardFooter>
    </Card>
  );
}
